import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
    Avatar,
    Box,
    ListItem,
    ListItemAvatar,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import { IChannel } from "@Interfaces";
import { ChannelActions } from "@Store";
import { ChannelMenu } from "@Components";
import useChannelProfileImg from "@Hooks/useChannelProfileImg";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
    getInactiveChannelItemStyles,
    getClosedChannelItemStyles,
} from "./ChannelItem.styles";

type ChannelItemProps = {
    channel: IChannel;
    selected?: boolean;
    hasChildren?: boolean;
    isExpanded?: boolean;
    onToggleChildren?: (channelId: string) => void;
    isChild?: boolean;
    isParentActive?: boolean;
    onChannelNavigate?: () => void;
    showChannelMenu?: boolean;
    showChannelAvatar?: boolean;
};

export const ChannelItem: React.FunctionComponent<ChannelItemProps> = ({
    channel,
    selected,
    hasChildren = false,
    isExpanded = false,
    onToggleChildren,
    isChild = false,
    isParentActive = false,
    onChannelNavigate,
    showChannelMenu = true,
    showChannelAvatar = true,
}) => {
    const { title, profile_image, _id } = channel;
    const isEventProfilePictureVisible =
        channel.settings?.display_profile_picture ?? true;

    const navigate = useNavigate();
    const dispatch = useDispatch<any>();
    const { channelId } = useParams<"channelId">();
    const theme = useTheme();
    const inactiveChannelStyles = getInactiveChannelItemStyles(theme);
    const endedChannelStyles = getClosedChannelItemStyles(theme);
    const channelProfileImage = useChannelProfileImg(
        isEventProfilePictureVisible ? profile_image?.public_url : undefined,
        isEventProfilePictureVisible ? profile_image?.fid : undefined
    );
    const isClosed = channel.closed === true;
    const isUnpublished = channel.active === false && !isClosed;
    const isExpandableParent = hasChildren && !isChild;

    const canSelect = !hasChildren;
    const computedSelected = canSelect
        ? selected !== undefined
            ? selected
            : channelId === _id
        : false;

    const handleClickOnListItem = () => {
        if (hasChildren) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            onToggleChildren?.(_id);
            return;
        }

        dispatch(ChannelActions.ensureChannelData(_id));

        if (channelId !== _id) {
            navigate(`/channel/${_id}`);
            onChannelNavigate?.();
            return;
        }

        onChannelNavigate?.();
    };

    useEffect(() => {
        if (channelId === _id && !hasChildren) {
            dispatch(ChannelActions.ensureChannelData(_id));
        }
    }, [_id, channelId, hasChildren, dispatch]);
    return (
        <>
            <ListItem
                button
                onClick={handleClickOnListItem}
                selected={computedSelected}
                sx={[
                    {
                        minHeight: theme.spacing(7),
                        alignItems: "center",
                        pr: 2,
                        "&.Mui-selected": {
                            backgroundColor: theme.palette.background.paper,
                        },
                        "&.Mui-selected:hover": {
                            backgroundColor: theme.palette.background.paper,
                        },
                    },
                    !isChild && {
                        boxShadow: "inset 0 -1px 0 rgba(0, 0, 0, 0.08)",
                        borderTop: `1px solid ${theme.palette.grey[200]}`,
                        backgroundColor: theme.palette.background.paper,
                    },
                    !isChild &&
                        hasChildren && {
                            backgroundColor: theme.palette.background.paper,
                        },
                    isChild && {
                        pl: 2,
                        minHeight: theme.spacing(3.5),
                        py: 0.75,
                        borderLeft: inactiveChannelStyles.borderLeft,
                        "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                        },
                    },
                    isChild &&
                        isUnpublished && {
                            borderLeft: inactiveChannelStyles.borderLeft,
                        },
                    isChild &&
                        isClosed && {
                            borderLeft: endedChannelStyles.borderLeft,
                        },
                    isChild &&
                        computedSelected &&
                        !isUnpublished &&
                        !isClosed && {
                            borderLeft: "3px solid #7986cb",
                        },
                    isChild &&
                        computedSelected &&
                        isUnpublished && {
                            borderLeft: "3px solid #979797",
                        },
                    isChild &&
                        computedSelected &&
                        isClosed && {
                            borderLeft: "3px solid #757575",
                        },
                ]}
            >
                {isExpandableParent && (
                    <ListItemIcon
                        sx={{
                            minWidth: theme.spacing(4),
                            display: "flex",
                            justifyContent: "center",
                            mr: theme.spacing(1),
                        }}
                    >
                        {isExpanded ? <ExpandMore /> : <ChevronRightIcon />}
                    </ListItemIcon>
                )}
                {showChannelAvatar &&
                !isExpandableParent &&
                isEventProfilePictureVisible ? (
                    <ListItemAvatar
                        sx={{
                            minWidth: theme.spacing(4),
                            display: "flex",
                            alignItems: "center",
                            mr: theme.spacing(2),
                        }}
                    >
                        <Avatar
                            src={channelProfileImage}
                            sx={[
                                {
                                    width: theme.spacing(4),
                                    height: theme.spacing(4),
                                },
                                isUnpublished && inactiveChannelStyles.avatar,
                                isClosed && endedChannelStyles.avatar,
                            ]}
                        />
                    </ListItemAvatar>
                ) : null}
                <ListItemText
                    sx={{
                        mr: showChannelMenu ? theme.spacing(6) : 0,
                        flex: "1 1 auto",
                    }}
                    primaryTypographyProps={{
                        sx: [
                            isClosed
                                ? {
                                      textTransform: "capitalize",
                                      width: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      overflow: "hidden",
                                  }
                                : {
                                      textTransform: "capitalize",
                                      width: "100%",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      wordBreak: "break-word",
                                  },
                            isChild && { fontSize: "0.95rem" },
                            !isChild &&
                                (computedSelected || isParentActive) && {
                                    fontWeight: "bold",
                                },
                            isUnpublished && {
                                color: inactiveChannelStyles.textColor,
                            },
                            isClosed && {
                                color: endedChannelStyles.textColor,
                            },
                        ],
                    }}
                    primary={
                        isClosed ? (
                            <>
                                <Box
                                    component="span"
                                    sx={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        flex: "1 1 auto",
                                        minWidth: 0,
                                    }}
                                >
                                    {title}
                                </Box>
                                <LockOutlinedIcon
                                    sx={{
                                        fontSize: "0.75rem",
                                        color: theme.palette.grey[500],
                                        ml: 0.5,
                                        flexShrink: 0,
                                        alignSelf: "center",
                                    }}
                                />
                            </>
                        ) : (
                            title
                        )
                    }
                />

                {showChannelMenu && <ChannelMenu channel={channel} />}
            </ListItem>
        </>
    );
};
